import { c as _c } from "react/compiler-runtime";
import { hashQueryKey, useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import * as z from "zod";
import { useApi } from "@/hooks/useApi";
import { type NamedOrg, orgApiPath } from "@/lib/named-org";
const SaveEnvelope = z.object({
  org: z.object({
    orgName: z.string(),
    instanceSlug: z.string()
  })
});
function pickFields<T extends object>(source: T, update: Partial<T>): Partial<T> {
  const picked: Partial<T> = {};
  for (const field of Object.keys(update) as (keyof T)[]) {
    picked[field] = source[field];
  }
  return picked;
}

/**
 * Query and mutation hooks for a member's per-org preferences that the
 * backend serves at `/user/github/orgs/{org}/{resource}`, every field
 * resolved to its default.
 *
 * Each control saves independently, so two mutations can overlap. Every
 * mutation callback only touches the fields its own update carried: a
 * delayed failure of one save must not roll back another save that already
 * landed.
 */
export function createUserOrgSettingsHooks<T extends object>({
  resource,
  errorMessage,
  parse
}: {
  resource: string;
  errorMessage: string;
  parse: (data: unknown) => T;
}) {
  function queryKey(org: NamedOrg | null | undefined) {
    return [resource, org] as const;
  }
  function isSaveFor(variables: unknown, target: NamedOrg) {
    const envelope = SaveEnvelope.safeParse(variables);
    if (!envelope.success) return false;
    return hashQueryKey([envelope.data.org]) === hashQueryKey([target]);
  }
  function useSettings(org, t0) {
    const $ = _c(10);
    let t1;
    if ($[0] !== t0) {
      t1 = t0 === undefined ? {} : t0;
      $[0] = t0;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const {
      suspense: t2
    } = t1;
    const suspense = t2 === undefined ? false : t2;
    const api = useApi();
    let t3;
    if ($[2] !== org) {
      t3 = queryKey(org);
      $[2] = org;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== api || $[5] !== org) {
      t4 = async () => {
        const response = await api.get(`${orgApiPath(org)}/${resource}`);
        return parse(response.data);
      };
      $[4] = api;
      $[5] = org;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    const t5 = !!org;
    let t6;
    if ($[7] !== suspense || $[8] !== t5) {
      t6 = {
        enabled: t5,
        suspense,
        useErrorBoundary: false
      };
      $[7] = suspense;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return useQuery(t3, t4, t6);
  }

  // Each save carries the org it was made for: the mutation observer outlives
  // an org switch, so a save issued later (e.g. a queued follow-up) must not
  // pick up the newly active org. In-flight siblings are matched on that
  // stamped org, not on mutationKey, which follows the active org.
  function useUpdateSettings(org) {
    const $ = _c(27);
    const api = useApi();
    const queryClient = useQueryClient();
    let t0;
    if ($[0] !== queryClient) {
      t0 = target => queryClient.isMutating({
        predicate: m => m.options.mutationKey?.[0] === resource && isSaveFor(m.state.variables, target)
      });
      $[0] = queryClient;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    const inFlightFor = t0;
    let t1;
    if ($[2] !== api) {
      t1 = async t2 => {
        const {
          org: target_0,
          update
        } = t2;
        const response = await api.put(`${orgApiPath(target_0)}/${resource}`, update);
        return parse(response.data);
      };
      $[2] = api;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== org) {
      t2 = queryKey(org);
      $[4] = org;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    let t4;
    if ($[6] !== queryClient) {
      t3 = async t5 => {
        const {
          org: target_1,
          update: update_0
        } = t5;
        if (!target_1) {
          return;
        }
        const key = queryKey(target_1);
        await queryClient.cancelQueries(key);
        const current = queryClient.getQueryData(key);
        if (!current) {
          return {
            org: target_1,
            previous: undefined
          };
        }
        queryClient.setQueryData(key, {
          ...current,
          ...update_0
        });
        return {
          org: target_1,
          previous: pickFields(current, update_0)
        };
      };
      t4 = (_error, _variables, context) => {
        if (context?.previous) {
          const key_0 = queryKey(context.org);
          const current_0 = queryClient.getQueryData(key_0);
          if (current_0) {
            queryClient.setQueryData(key_0, {
              ...current_0,
              ...context.previous
            });
          }
        }
        toast.error(errorMessage);
      };
      $[6] = queryClient;
      $[7] = t3;
      $[8] = t4;
    } else {
      t3 = $[7];
      t4 = $[8];
    }
    let t5;
    let t6;
    if ($[9] !== inFlightFor || $[10] !== queryClient) {
      t5 = (data, t7, context_0) => {
        const {
          update: update_1
        } = t7;
        if (!context_0) {
          return;
        }
        const key_1 = queryKey(context_0.org);
        if (inFlightFor(context_0.org) > 1) {
          return;
        }
        const current_1 = queryClient.getQueryData(key_1);
        queryClient.setQueryData(key_1, current_1 ? {
          ...current_1,
          ...pickFields(data, update_1)
        } : data);
      };
      t6 = (_data, _error_0, _variables_0, context_1) => {
        if (!context_1) {
          return;
        }
        const key_2 = queryKey(context_1.org);
        if (inFlightFor(context_1.org) <= 1) {
          queryClient.invalidateQueries(key_2);
        }
      };
      $[9] = inFlightFor;
      $[10] = queryClient;
      $[11] = t5;
      $[12] = t6;
    } else {
      t5 = $[11];
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
      t7 = {
        mutationKey: t2,
        onMutate: t3,
        onError: t4,
        onSuccess: t5,
        onSettled: t6
      };
      $[13] = t2;
      $[14] = t3;
      $[15] = t4;
      $[16] = t5;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    const mutation = useMutation(t1, t7);
    let t8;
    let t9;
    if ($[19] !== mutation || $[20] !== org) {
      t8 = update_2 => mutation.mutate({
        org,
        update: update_2
      });
      t9 = update_3 => mutation.mutateAsync({
        org,
        update: update_3
      });
      $[19] = mutation;
      $[20] = org;
      $[21] = t8;
      $[22] = t9;
    } else {
      t8 = $[21];
      t9 = $[22];
    }
    let t10;
    if ($[23] !== mutation || $[24] !== t8 || $[25] !== t9) {
      t10 = {
        ...mutation,
        mutate: t8,
        mutateAsync: t9
      };
      $[23] = mutation;
      $[24] = t8;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    return t10;
  }
  return {
    queryKey,
    useSettings,
    useUpdateSettings
  };
}